import { useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { CheckboxWithLabel } from "@volca/ui/components/ui/checkbox-with-label";
import {
  CommandGroup,
  CommandInput,
  CommandList,
} from "@volca/ui/components/ui/command";
import { DialogFooter } from "@volca/ui/components/ui/dialog";
import { Spinner } from "@volca/ui/components/ui/spinner";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { toast } from "@volca/ui/lib/toast";
import {
  useMatchLinePricebookItem,
  useSearchPricebookItems,
} from "@/accounts-payable/query";
import type {
  ApDocumentType,
  ApInvoiceItem,
  ApPricebookItemOption,
} from "@/accounts-payable/types";
import {
  documentTypeLabel,
  formatDollars,
  hasPricebookMatch,
  matchedPricebookItemLabel,
  pricebookItemLabel,
  pricebookOptionMeta,
  pricebookSearchStatusMessage,
} from "@/accounts-payable/utils";
import { getErrorMessage } from "@/core/api/errors";
import { useDebounce } from "@/core/hooks";
import { PickerCommand, PickerItem } from "./picker-command";

/** The search reads our own pricebook copy, so it can afford to be quick. */
const SEARCH_DEBOUNCE_MS = 250;

export const PricebookMatchForm = ({
  businessId,
  documentId,
  documentType,
  agentRunId,
  item,
  matchLine,
  onClose,
}: {
  businessId: string | null;
  documentId: string;
  documentType: ApDocumentType;
  agentRunId: string | null;
  item: ApInvoiceItem;
  matchLine: ReturnType<typeof useMatchLinePricebookItem>;
  onClose: () => void;
}) => {
  // The server can give the pick to the other lines sharing this one's SKU
  // (usually the same part) and says how many it would reach. Some vendors put
  // a product family in the SKU and the size in the description, so the
  // person picking can keep it to this line.
  const sameSkuLineCount = item.same_sku_line_count ?? 1;
  const [applyToSameSkuLines, setApplyToSameSkuLines] = useState(true);
  const lineCount = applyToSameSkuLines ? sameSkuLineCount : 1;
  // Seeded with the SKU: it is what the automatic match searched by first.
  const [query, setQuery] = useState(item.sku ?? "");
  const [selected, setSelected] = useState<ApPricebookItemOption | null>(null);
  const debouncedQuery = useDebounce(query, SEARCH_DEBOUNCE_MS);
  const {
    data: options = [],
    isLoading,
    isError,
    isPlaceholderData,
  } = useSearchPricebookItems(businessId, debouncedQuery);

  // A selection belongs to the results it was picked from (see the job match).
  const onQueryChange = (value: string) => {
    setQuery(value);
    if (value.trim() !== query.trim()) setSelected(null);
  };

  const isSearchPending =
    isLoading || isPlaceholderData || query !== debouncedQuery;
  const statusMessage = pricebookSearchStatusMessage({
    query,
    isLoading: isSearchPending,
    isError,
    resultCount: options.length,
  });

  const save = (pricebookItem: ApPricebookItemOption | null) =>
    matchLine.mutate(
      {
        documentId,
        lineItemId: item.id,
        pricebookItemId: pricebookItem?.id ?? null,
        ...(sameSkuLineCount > 1 && { applyToSameSkuLines }),
      },
      {
        onSuccess: (document) => {
          const lines = lineCount > 1 ? `${lineCount} lines` : "Line";
          const outcome = pricebookItem
            ? `${lines} matched to ${pricebookItemLabel(pricebookItem)}.`
            : `${lines} marked as not in the pricebook.`;
          // The server rejects the agent's open proposals and queues a run
          // that reads the pick, after a short wait so several edits share it.
          const rerunQueued =
            document.agent_run_id !== null &&
            document.agent_run_id !== agentRunId;
          toast.success(
            rerunQueued
              ? `${outcome} The agent will re-check this ${documentTypeLabel(documentType).lower} against it in a couple of minutes.`
              : outcome,
          );
          onClose();
        },
        onError: (error: unknown) =>
          toast.error("Couldn’t change the pricebook item", {
            description: getErrorMessage(error),
          }),
      },
    );

  return (
    <>
      <Stack align="start" gap={4} className="w-full">
        <Stack className="w-full" align="start" gap={0}>
          <Text>
            {item.quantity} × {item.description}
          </Text>
          <Text variant="caption" tone="muted">
            {[item.sku, formatDollars(item.unit_cost)]
              .filter(Boolean)
              .join(" · ")}
            {hasPricebookMatch(item) &&
              ` · now ${matchedPricebookItemLabel(item)}`}
          </Text>
        </Stack>
        {sameSkuLineCount > 1 && (
          <CheckboxWithLabel
            id={`same-sku-lines-${item.id}`}
            label={`Also apply to the ${sameSkuLineCount - 1} other ${sameSkuLineCount === 2 ? "line" : "lines"} invoiced as ${item.sku}`}
            checked={applyToSameSkuLines}
            disabled={matchLine.isPending}
            onCheckedChange={(checked) =>
              setApplyToSameSkuLines(checked === true)
            }
          />
        )}
        <PickerCommand label="Search the pricebook">
          <CommandInput
            value={query}
            onValueChange={onQueryChange}
            disabled={matchLine.isPending}
            placeholder="Search by code, name, or description"
          />
          <CommandList className="max-h-64">
            {statusMessage ? (
              <Stack
                gap={4}
                align="center"
                role="status"
                className="w-full px-4 py-6 text-center text-sm text-subtle"
              >
                {statusMessage}
              </Stack>
            ) : (
              <CommandGroup>
                {options.map((option) => {
                  const isSelected = selected?.id === option.id;
                  const isCurrent =
                    option.external_id === item.matched_pricebook_item_id &&
                    option.item_type === item.matched_pricebook_item_type;
                  return (
                    <PickerItem
                      key={option.id}
                      value={option.id}
                      selected={isSelected}
                      disabled={matchLine.isPending}
                      onSelect={() => setSelected(option)}
                    >
                      <Stack align="start" gap={0} className="min-w-0 flex-1">
                        <Stack
                          direction="row"
                          gap={2}
                          align="baseline"
                          className="flex-wrap"
                        >
                          <span className="font-semibold">
                            {option.name ?? option.code ?? "Unnamed item"}
                          </span>
                          {isCurrent && (
                            <span className="text-xs text-subtle">
                              Current match
                            </span>
                          )}
                        </Stack>
                        <span className="text-xs text-subtle">
                          {pricebookOptionMeta(option).join(" · ")}
                        </span>
                      </Stack>
                    </PickerItem>
                  );
                })}
              </CommandGroup>
            )}
          </CommandList>
        </PickerCommand>
      </Stack>
      {/* One row at every width: the default footer stacks in reverse on
          phones, which pushed the clear action under the buttons. */}
      <DialogFooter className="flex-row flex-wrap items-center justify-between gap-3 sm:justify-between sm:gap-3">
        {/* Clearing is for a line that isn't stocked: it keeps it unmatched
            and stops any pending cost review raised against the wrong item.
            -ml-2.5 takes back the ghost padding, so its text lines up with
            the content above. */}
        {hasPricebookMatch(item) ? (
          <Button
            variant="ghost"
            className="-ml-2.5"
            onClick={() => save(null)}
            disabled={matchLine.isPending}
          >
            Not in the pricebook
          </Button>
        ) : (
          <Stack align="start" gap={4} />
        )}
        <Stack align="start" direction="row" gap={2}>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={matchLine.isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={() => selected && save(selected)}
            disabled={!selected || matchLine.isPending}
            aria-busy={matchLine.isPending || undefined}
          >
            {matchLine.isPending && (
              <Spinner aria-hidden data-icon="inline-start" />
            )}
            {/* Not the item's name: pricebook names run long, and the pick is
                already checked in the list above. */}
            Match to item
          </Button>
        </Stack>
      </DialogFooter>
    </>
  );
};
